import React, { useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { Loader } from "semantic-ui-react";

// Actions
import useCustomerInteraction from "@common/components/hooks/useCustomerInteraction";
import LoginAction from "@common/redux/actions/LoginAction";

// Hooks
import useEventDetailsGrid from "@hooks/useEventDetailsGrid";
import useComplianceView from "@hooks/useComplianceView.js";
import useCustomerReview from "@common/components/hooks/useCustomerReview.js";
import useLinkedAlert from "@common/components/hooks/useLinkedAlert.js";

// APIs & Helpers
import CustomerInteractionClient from "@common/api/CustomerInteractionClient";
import { handleError } from "@common/components/helpers/error";
import { parseCustomerInteractionResponse } from "@common/components/helpers/compliance.js";
import { fetchInteractionOptions } from "@common/components/helpers/CustomerInteraction/fetchInteractionOptions";

// Components
import ComplianceHeader from "../ComplianceHeader.js";
import CustomerInteractionForm from "@common/components/shared/CustomerInteraction/CustomerInteractionForm.js";
import useLinkSession from "@common/components/hooks/useLinkSession.js";

const CustomerInteractionContainer = () => {
  const {
    activeRow: {
      event: { id },
    },
  } = useEventDetailsGrid();
  const { formLoading, setViewData } = useComplianceView();

  const { onRecordChange, open } = useCustomerReview();

  const { setState } = useLinkedAlert();

  const { getComplianceSessionsByGuids } = useLinkSession();

  const {
    methods,
    methods: { reset },
  } = useCustomerInteraction();

  useEffect(fetchInteractionOptions, []);

  const loadRecord = (isRead = true) => {
    if (!id) {
      return;
    }

    setViewData({ formLoading: true });
    const fnRead = isRead
      ? CustomerInteractionClient.Read
      : CustomerInteractionClient.ReadForUpdate;

    fnRead(id).then(
      ({ record }) => {
        const {
          site: { guid },
          sessionGuids,
          alerts,
        } = record;
        LoginAction.dispatch({ siteGuid: guid, isInReadMode: isRead });
        onRecordChange(record);
        const parsedRecord = parseCustomerInteractionResponse(record);
        reset(parsedRecord);
        getComplianceSessionsByGuids("", sessionGuids);
        setState({ linkedAlertData: alerts });
        setViewData({ formLoading: false });
      },
      (error) => {
        setViewData({ formLoading: false });
        handleError(error);
      },
    );
  };

  useEffect(loadRecord, [id, open, reset]);

  if (formLoading) {
    return <Loader active />;
  }

  return (
    <FormProvider {...methods}>
      <ComplianceHeader title="Customer Interaction" loadRecord={loadRecord} />
      <CustomerInteractionForm />
    </FormProvider>
  );
};

export default CustomerInteractionContainer;
